import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { iconFor } from "../lib/icons";
import { mediaVariant } from "../lib/catalogueApi";

// T58: homepage promo row (Admin > Content > Homepage promo row). 1024px+: 2fr 1fr 1fr. 641-1023px: large card on top, the two
// small cards side by side. Phones: stacked. `phone` forces the phone layout (admin preview).
const presetSrc = (k, w) => `/brand/promo/${k}-${w}.webp`;
const SCALLOP = (() => {
  const pts = [];
  for (let i = 0; i < 240; i++) {
    const a = (i / 240) * Math.PI * 2, r = 46 + 2.6 * Math.cos(20 * a);
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`);
  }
  return `M${pts.join("L")}Z`;
})();

// Same rules as the server (promo.stamp) so the admin preview matches the storefront.
export function previewPromo(promo, live) {
  const lg = promo.large || {}, s = lg.stamp || {}, mx = live?.max_pct;
  let stamp = { show: false };
  if (s.visible !== false && mx != null) {
    let pct = mx, source = "auto";
    if (s.mode === "manual" && s.manual_pct) [pct, source] = s.manual_pct <= mx ? [s.manual_pct, "manual"] : [mx, "auto_fallback"];
    stamp = { show: pct >= (s.hide_below ?? 5), pct, source };
  }
  return { large: lg.visible !== false && live?.orderable ? { ...lg, stamp } : null, small: (promo.small || []).filter((c) => c.visible !== false) };
}

export function Stamp({ pct, className = "" }) {
  return (
    <div role="img" aria-label={`Save up to ${pct}%`} data-testid="promo-stamp" className={`pointer-events-none absolute -rotate-12 [container-type:inline-size] drop-shadow-[0_6px_14px_rgba(10,132,255,0.35)] ${className}`}>
      <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false" className="absolute inset-0 h-full w-full">
        <path d={SCALLOP} fill="var(--brand-accent)" />
        <circle cx="50" cy="50" r="38" fill="none" stroke="#fff" strokeOpacity="0.45" strokeWidth="1.4" />
      </svg>
      <span aria-hidden="true" className="absolute inset-0 flex flex-col items-center justify-center text-center leading-none text-white">
        <span className="text-[13cqw] font-extrabold tracking-[0.12em]">SAVE</span>
        <span className="mt-[2cqw] text-[10cqw] font-semibold text-white/90">up to</span>
        <span className={`mt-[2cqw] font-extrabold ${pct >= 100 ? "text-[20cqw]" : "text-[25cqw]"}`} data-testid="promo-stamp-pct">{pct}%</span>
      </span>
    </div>
  );
}

function Large({ c, r }) {
  const img = c.image || {};
  const a = Math.max(0, Math.min(90, c.overlay ?? 70));
  const mix = (p) => `color-mix(in srgb, var(--brand-primary) ${Math.min(100, Math.round(p))}%, transparent)`;
  const grad = `linear-gradient(90deg, ${mix(a + 12)} 0%, ${mix(a)} 38%, ${mix(a * 0.25)} 72%, transparent 100%)`;
  const src = img.media_id ? mediaVariant(img.media_id, 1200, "jpeg") : presetSrc(img.preset || "desk-laptop", 1200);
  return (
    <article data-testid="promo-large" className={r("relative isolate flex min-h-[240px] overflow-hidden rounded-2xl bg-[var(--brand-primary)] shadow-[0_8px_24px_rgba(11,42,91,0.14)]",
      "min-[641px]:col-span-2 min-[641px]:min-h-[260px] lg:col-span-1 lg:min-h-[236px] lg:transition-[transform,box-shadow] lg:duration-200 lg:hover:-translate-y-0.5 lg:hover:shadow-[0_14px_32px_rgba(11,42,91,0.2)] motion-reduce:transition-none lg:motion-reduce:hover:translate-y-0")}>
      <img src={src} srcSet={img.media_id ? undefined : `${presetSrc(img.preset || "desk-laptop", 640)} 640w, ${src} 1200w`} sizes="(min-width: 1024px) 560px, 100vw"
        alt="" loading="lazy" decoding="async" width="1200" height="805" className="absolute inset-0 -z-10 h-full w-full object-cover"
        style={{ objectPosition: `${img.focal_x ?? 50}% ${img.focal_y ?? 50}%` }} data-testid="promo-large-image" />
      <div aria-hidden="true" className="absolute inset-0 -z-10" style={{ background: grad }} />
      <div className={r("flex max-w-[calc(100%-116px)] flex-col justify-center p-6", "min-[641px]:max-w-[60%] min-[641px]:p-8")}>
        <h2 className="text-[22px] font-bold leading-tight text-white lg:text-2xl" data-testid="promo-large-title">{c.title}</h2>
        {c.sub && <p className="mt-1.5 text-[15px] leading-snug text-white/80 [text-wrap:balance]" data-testid="promo-large-sub">{c.sub}</p>}
        <Link to={c.cta_link} data-testid="promo-large-cta"
          className="mt-5 inline-flex h-11 w-fit items-center gap-2 rounded-full bg-[var(--brand-accent)] px-5 text-sm font-semibold !text-white shadow-[0_6px_16px_rgba(10,132,255,0.35)] transition-[filter] duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
          {c.cta_label}<ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      {c.stamp?.show && <Stamp pct={c.stamp.pct} className={r("right-3 top-3 h-[84px] w-[84px]", "min-[641px]:right-7 min-[641px]:top-6 min-[641px]:h-[108px] min-[641px]:w-[108px] lg:right-[7%] lg:top-[12%] lg:h-[112px] lg:w-[112px]")} />}
    </article>
  );
}

function Small({ c, r }) {
  const Icon = iconFor(c.icon);
  return (
    <article data-testid={`promo-card-${c.key}`} className={r("flex gap-4 rounded-2xl border border-[color-mix(in_srgb,var(--brand-accent)_14%,white)] bg-[color-mix(in_srgb,var(--brand-accent)_6%,white)] p-5 shadow-[0_4px_16px_rgba(11,42,91,0.06)]",
      "min-[641px]:p-6 lg:flex-col lg:gap-3 lg:p-5 xl:flex-row lg:transition-[transform,box-shadow] lg:duration-200 lg:hover:-translate-y-0.5 lg:hover:shadow-[0_10px_24px_rgba(11,42,91,0.1)] motion-reduce:transition-none lg:motion-reduce:hover:translate-y-0")}>
      <Icon aria-hidden="true" focusable="false" strokeWidth={1.75} className="h-9 w-9 shrink-0 text-[var(--brand-accent)] lg:h-8 lg:w-8" data-testid={`promo-card-icon-${c.key}`} />
      <div className="flex min-w-0 flex-col">
        <h3 className="text-[17px] font-semibold leading-snug text-[var(--brand-primary)] [text-wrap:balance] lg:text-[15px]" data-testid={`promo-card-title-${c.key}`}>{c.title}</h3>
        {c.sub && <p className="mt-1 text-sm leading-snug text-slate-600 [text-wrap:balance]" data-testid={`promo-card-sub-${c.key}`}>{c.sub}</p>}
        <div className="mt-auto pt-4">
          <Link to={c.cta_link} data-testid={`promo-card-cta-${c.key}`}
            className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 bg-white px-5 lg:px-4 xl:px-5 text-sm font-semibold text-[var(--brand-link)] shadow-sm transition-[border-color] duration-200 hover:border-[var(--brand-accent)]">
            {c.cta_label}<ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}

function PromoSkeleton() {
  const b = "animate-pulse rounded-2xl border border-slate-200 bg-white motion-reduce:animate-none";
  return (
    <div aria-hidden="true" data-testid="home-promo-skeleton" className="my-10 grid grid-cols-1 gap-4 min-[641px]:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] lg:gap-5">
      <div className={`${b} min-h-[240px] min-[641px]:col-span-2 min-[641px]:min-h-[260px] lg:col-span-1 lg:min-h-[236px]`} />
      <div className={`${b} min-h-[176px] lg:min-h-[236px]`} /><div className={`${b} min-h-[176px] lg:min-h-[236px]`} />
    </div>
  );
}

export function HomePromo({ promo, loading = false, phone = false, preview = false }) {
  const r = (base, wide) => (phone ? base : `${base} ${wide}`);
  if (loading) return <PromoSkeleton />;
  if (!promo || (!promo.large && !promo.small?.length)) return null;
  return (
    <section aria-label="Offers and services" data-testid={preview ? (phone ? "preview-promo-phone" : "preview-promo") : "home-promo"}
      className={r("my-10 grid grid-cols-1 gap-4", promo.large ? "min-[641px]:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] lg:gap-5" : "min-[641px]:grid-cols-2 lg:gap-5")}>
      {promo.large && <Large c={promo.large} r={r} />}
      {promo.small.map((c) => <Small key={c.key} c={c} r={r} />)}
    </section>
  );
}
